import React, { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { PaginateList } from 'src/app/common/types/common-types';
import { DistributionRule } from 'src/app/modules/PulseLeads/types/distribution-rule-types';
import PruTable from 'src/app/common/components/PruTable/PruTable';
import { distributionRulePath } from '../../DistributionRuleRoutes';
import { getDefaultDisplayDate } from 'src/app/common/utils';
import ItemDeleteDialog from 'src/app/common/components/ItemDeleteDialog';
import { deleteDistributionRule } from 'src/app/modules/PulseLeads/network/distributionRuleCrud';
import { appendAlertItem, AlertType } from '@pruforce/common-adminweb-sdk';

type DistributionRuleListProps = {
  isLoading: boolean;
  distributionRuleList?: PaginateList<DistributionRule>;
  onRefresh: () => void;
  onChangePage: (page: number, rowsPerPage: number) => void;
};

type DialogState = {
  open: boolean;
  id: string;
};

const DistributionRuleList: FC<DistributionRuleListProps> = ({
  isLoading,
  distributionRuleList,
  onRefresh,
  onChangePage,
}) => {
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    id: '',
  });

  const onDelete = async (id: string) => {
    try {
      await deleteDistributionRule(id, dispatch);
      dispatch(
        appendAlertItem([
          {
            severity: AlertType.SUCCESS,
            title: 'Success',
            content: `Distribution Rule deleted successfully - ${id}`,
          },
        ]),
      );
    } catch (err) {}
  };

  return (
    <>
      <ItemDeleteDialog
        key={`delete-distribution-item-dialog-${dialogState.open}`}
        open={dialogState.open}
        onClose={() => setDialogState({ open: false, id: '' })}
        onRefresh={onRefresh}
        onDelete={() => onDelete(dialogState.id)}
      />
      <PruTable
        title={Translation('pulseleads.distributionRule.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('pulseleads.distributionRule.newRule'),
            onClick: () => history.push(`${distributionRulePath}/create`),
          },
        ]}
        disableBulkSelect
        operationDef={[
          {
            title: Translation('section.common.operation.edit'),
            tooltipText: 'Edit Distribution Rule',
            onClick: (row) => history.push(`${distributionRulePath}/edit/${row._id}`),
          },
          {
            title: Translation('section.common.operation.delete'),
            tooltipText: 'Delete Distribution Rule',
            onClick: (row) => setDialogState({ open: true, id: row._id }),
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: (row) => '',
          },
          {
            keyIndex: 'name',
            displayName: Translation('pulseleads.distributionRule.common.name'),
            renderData: (row) => row.name || '-',
          },
          {
            keyIndex: 'method',
            align: 'center',
            displayName: Translation('pulseleads.distributionRule.common.method'),
            renderData: (row) => row.method || '-',
          },
          {
            keyIndex: 'description',
            align: 'center',
            displayName: Translation('pulseleads.distributionRule.common.description'),
            renderData: (row) => row.description || '-',
          },
          {
            keyIndex: 'updatedBy',
            align: 'center',
            displayName: Translation('pulseleads.distributionRule.common.updatedBy'),
            renderData: (row) => row.updatedBy || '-',
          },
          {
            keyIndex: 'createdAt',
            align: 'center',
            displayName: Translation('pulseleads.distributionRule.common.createdAt'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.createdAt), 'datetime'),
          },
          {
            keyIndex: 'updatedAt',
            align: 'center',
            displayName: Translation('pulseleads.distributionRule.common.updatedAt'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.updatedAt), 'datetime'),
          },
        ]}
        isLoading={isLoading}
        onRefresh={onRefresh}
        dataSource={distributionRuleList?.docs}
        totalPages={distributionRuleList?.totalPages}
        totalRecords={distributionRuleList?.totalDocs}
        onChangePage={onChangePage}
      />
    </>
  );
};

export default DistributionRuleList;
